import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatUKDate } from "@/lib/date";
import { todayISO } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Copy, RefreshCw } from "lucide-react";

const TEMPLATES = [
  { key: "fixtures", label: "Today's fixtures" },
  { key: "kickoff", label: "Kickoff reminder" },
  { key: "results", label: "Match results" },
  { key: "daily_table", label: "Daily league table" },
  { key: "overall_table", label: "Overall league table" },
];

function formatDateUK(iso) {
  try {
    return formatUKDate(iso, { weekday: "long", day: "numeric", month: "long" });
  } catch {
    return iso;
  }
}

function rankEmoji(rank) {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return `${rank}.`;
}

/**
 * iter157 — WhatsApp broadcast CTAs are now branded per pub.
 *
 * When the panel is rendered on `/c/<slug>/editor`, every "👉 link"
 * in every template points at `<origin>/<slug>` (i.e.
 * `play.amblemead.biz/<slug>` on production). The "Full table" CTA
 * points at the branded scoped route `<origin>/c/<slug>/leaderboard`.
 *
 * On the master `/editor` surface (no slug in the URL) we fall back to
 * the bare origin so the panel still generates something useful for
 * super-admin broadcasts.
 */
function buildFixturesMessage(matches, dateIso, urls) {
  if (!matches || matches.length === 0) {
    return `⚽ *Dugsy's Football Challenge*\n_${formatDateUK(dateIso)}_\n\nNo fixtures scheduled today.`;
  }
  const lines = matches
    .slice()
    .sort((a, b) => (a.kickoff || "").localeCompare(b.kickoff || ""))
    .map((m) => `🕒 ${m.kickoff}  ${m.home_team} vs ${m.away_team}`)
    .join("\n");
  return (
    `⚽ *Dugsy's Football Challenge — Today's fixtures*\n` +
    `_${formatDateUK(dateIso)}_\n\n` +
    `${lines}\n\n` +
    `Get your predictions in before kickoff 👉 ${urls.landing}`
  );
}

function buildKickoffReminderMessage(matches, dateIso, urls) {
  const open = (matches || []).filter((m) => !m.kickoff_passed && m.status !== "finished");
  if (open.length === 0) {
    return `⏰ *Predictions reminder*\n_${formatDateUK(dateIso)}_\n\nAll of today's matches are closed. See you tomorrow!`;
  }
  const lines = open
    .slice()
    .sort((a, b) => (a.kickoff || "").localeCompare(b.kickoff || ""))
    .map((m) => `⏰ ${m.kickoff}  ${m.home_team} vs ${m.away_team}`)
    .join("\n");
  return (
    `⏰ *Last chance to predict!*\n` +
    `_${formatDateUK(dateIso)}_\n\n` +
    `${lines}\n\n` +
    `Lock in your scores 👉 ${urls.landing}`
  );
}

function buildResultsMessage(matches, dailyRows, dateIso, urls) {
  const finished = (matches || []).filter((m) => m.status === "finished" && m.actual);
  if (finished.length === 0) {
    return (
      `📋 *Full-time results*\n` +
      `_${formatDateUK(dateIso)}_\n\n` +
      `No results entered yet for today.`
    );
  }
  const blocks = finished
    .slice()
    .sort((a, b) => (a.kickoff || "").localeCompare(b.kickoff || ""))
    .map((m) => {
      const a = m.actual || {};
      return (
        `🔵 *${m.home_team} vs ${m.away_team}*  _(${m.kickoff})_\n` +
        `Goals ${a.goals ?? "—"} · Shots on target ${a.shots_on_target ?? "—"} · Corners ${a.corners ?? "—"} · Offsides ${a.offsides ?? "—"} · Throw-ins ${a.throw_ins ?? "—"} · Fouls ${a.fouls ?? "—"}`
      );
    })
    .join("\n\n");

  const leader = (dailyRows || [])[0];
  const leaderLine = leader
    ? `\n\n🏆 Today's leader: *${leader.user_name}* on ${leader.total_points} pts`
    : "";

  return (
    `📋 *Full-time results*\n` +
    `_${formatDateUK(dateIso)}_\n\n` +
    `${blocks}` +
    `${leaderLine}\n\n` +
    `Full table 👉 ${urls.leaderboard}`
  );
}

function buildLeagueTableMessage({ rows, dateIso, scope, urls }) {
  const title = scope === "daily" ? "Today's league table" : "Overall league table";
  const subtitle = scope === "daily" ? `_${formatDateUK(dateIso)}_` : "_All matches · all-time_";
  if (!rows || rows.length === 0) {
    return `🏆 *${title}*\n${subtitle}\n\nNo scores yet.`;
  }
  const top = rows.slice(0, 10);
  const lines = top
    .map((r, i) => {
      const rank = i + 1;
      return `${rankEmoji(rank)} ${r.user_name} — *${r.total_points} pts*`;
    })
    .join("\n");
  return (
    `🏆 *${title}*\n` +
    `${subtitle}\n\n` +
    `${lines}\n\n` +
    `Full table 👉 ${urls.leaderboard}`
  );
}

export default function BroadcastPanel() {
  const { slug } = useParams();
  const [date, setDate] = useState(todayISO());
  const [active, setActive] = useState("fixtures");
  const [matches, setMatches] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [overallRows, setOverallRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editedDraft, setEditedDraft] = useState(null); // null = not edited, follow generated

  // iter157 — CTAs use the live origin (play.amblemead.biz in prod)
  // + the slug from the URL when on `/c/<slug>/editor`. Master-editor
  // signups (no slug) fall back to the plain origin.
  const urls = useMemo(() => {
    const origin =
      (typeof window !== "undefined" && window.location?.origin) ||
      "https://play.amblemead.biz";
    if (slug) {
      return {
        landing: `${origin}/${slug}`,
        leaderboard: `${origin}/c/${slug}/leaderboard`,
      };
    }
    return {
      landing: origin,
      leaderboard: `${origin}/leaderboard`,
    };
  }, [slug]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // iter159 — pass `scope_slug` to the leaderboard fetches so the
      // draft table reflects THIS pub's players only. Without this the
      // daily / overall table templates render the global leaderboard
      // (or empty on a scoped-only pub) instead of the pub's own.
      const scope = slug ? `&scope_slug=${encodeURIComponent(slug)}` : "";
      const scopeOnly = slug ? `?scope_slug=${encodeURIComponent(slug)}` : "";
      const [mRes, dRes, oRes] = await Promise.all([
        api.get(`/matches?date=${date}`),
        api.get(`/leaderboard?date=${date}${scope}`),
        api.get(`/leaderboard${scopeOnly}`),
      ]);
      setMatches(mRes.data || []);
      setDailyRows(dRes.data || []);
      setOverallRows(oRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [date, slug]);

  useEffect(() => {
    load();
  }, [load]);

  const generated = useMemo(() => {
    switch (active) {
      case "fixtures":
        return buildFixturesMessage(matches, date, urls);
      case "kickoff":
        return buildKickoffReminderMessage(matches, date, urls);
      case "results":
        return buildResultsMessage(matches, dailyRows, date, urls);
      case "daily_table":
        return buildLeagueTableMessage({ rows: dailyRows, dateIso: date, scope: "daily", urls });
      case "overall_table":
        return buildLeagueTableMessage({ rows: overallRows, dateIso: date, scope: "overall", urls });
      default:
        return "";
    }
  }, [active, matches, dailyRows, overallRows, date, urls]);

  const draft = editedDraft ?? generated;

  const switchTemplate = (key) => {
    setActive(key);
    setEditedDraft(null);
  };

  const refreshDraft = () => {
    setEditedDraft(null);
    toast.success("Message refreshed from latest data");
  };

  const copy = async () => {
    const text = draft;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Copied — paste it into your WhatsApp group");
    } catch (e) {
      toast.error("Couldn't copy automatically — select and copy manually");
    }
  };

  return (
    <section className="mt-16" data-testid="admin-broadcast-panel">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
            <MessageCircle className="h-5 w-5" /> WhatsApp broadcast
          </h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Generate a ready-to-paste message for your WhatsApp group. Edit if you like, then copy and paste.
          </p>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Date</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => { setDate(e.target.value); setEditedDraft(null); }}
            data-testid="broadcast-date"
            className="mt-2 h-11 w-48 rounded-sm border-zinc-300"
          />
        </div>
      </div>

      <div className="border border-zinc-200 rounded-sm bg-white">
        {/* Template selector */}
        <div className="flex flex-wrap gap-2 p-4 border-b border-zinc-200">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              onClick={() => switchTemplate(t.key)}
              data-testid={`broadcast-tab-${t.key}`}
              className={`px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                active === t.key
                  ? "bg-zinc-950 text-white"
                  : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Editable message */}
        <div className="p-4">
          {loading ? (
            <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
          ) : (
            <textarea
              value={draft}
              onChange={(e) => setEditedDraft(e.target.value)}
              data-testid="broadcast-textarea"
              rows={Math.min(20, Math.max(8, draft.split("\n").length + 1))}
              className="w-full font-mono text-sm leading-relaxed p-4 border border-zinc-200 rounded-sm bg-zinc-50 focus:bg-white focus:border-zinc-400 focus:outline-none whitespace-pre-wrap"
              spellCheck={false}
            />
          )}

          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-zinc-500">
              WhatsApp formatting: *bold* · _italic_ · ~strike~. Single line breaks are preserved.
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={refreshDraft}
                data-testid="broadcast-refresh"
                className="h-11 px-4 rounded-sm border-zinc-300 hover:bg-zinc-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Reset
              </Button>
              <Button
                type="button"
                onClick={copy}
                data-testid="broadcast-copy"
                className="h-11 px-6 rounded-sm bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold tracking-tight active:scale-[0.98]"
              >
                <Copy className="h-4 w-4 mr-2" /> Copy for WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
